import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import MainLayout from "../../components/layout/MainLayout";
import CommonTable from "../../components/common/CommonTable";
import api from "../../services/api";
import Swal from "sweetalert2";
import "./Payroll.css";

function GeneratePayroll() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [versionOptions, setVersionOptions] = useState([]);
  const [selectedEngineVersionId, setSelectedEngineVersionId] = useState("");
  const [existingRuns, setExistingRuns] = useState([]);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = months[new Date().getMonth()];

  const currentMonthIndex = today.getMonth() - 1; // 0-based

  // Financial year starts in April (index 3)
  const financialStartYear =
    currentMonthIndex >= 3 ? currentYear : currentYear - 1;

  const financialMonths = [];

  // Case 1: April–December
  if (currentMonthIndex >= 3) {
    for (let i = 3; i <= currentMonthIndex; i++) {
      financialMonths.push({
        month: months[i],
        year: financialStartYear,
      });
    }
  }
  // Case 2: January–March
  else {
    // April–December of previous year
    for (let i = 3; i < 12; i++) {
      financialMonths.push({
        month: months[i],
        year: financialStartYear,
      });
    }

    // January–current month of current year
    for (let i = 0; i <= currentMonthIndex; i++) {
      financialMonths.push({
        month: months[i],
        year: financialStartYear + 1,
      });
    }
  }

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async (monthKey = "") => {
    try {
      const response = await api.get("/payroll/generate", {
        params: monthKey ? { selected_month: monthKey } : {},
      });

      if (response.data.success) {
        const payload = response.data.data || {};
        setEmployees(payload.emp_attendance || []);
        setSelectedMonth(payload.selected_month_key || monthKey || "");
        setVersionOptions(payload.version_options || []);
        setSelectedEngineVersionId(
          String(payload.selected_engine_version_id || payload.latest_version_id || ""),
        );
        setExistingRuns(payload.existing_runs || []);
      }
    } catch (error) {
      console.error("Failed to fetch payroll data:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load payroll data",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = async (e) => {
    const value = e.target.value;
    setSelectedMonth(value);

    if (!value) {
      fetchInitialData();
      return;
    }
    setLoading(true);
    await fetchInitialData(value);
    setLoading(false);
  };

  const handleProcessPayroll = () => {
    if (employees.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Data",
        text: "No employees to process payroll",
      });
      return;
    }

    // Navigate to process payroll page with selected month
    navigate("/payroll/process", {
      state: {
        selectedMonth: selectedMonth || `${currentMonth}_${currentYear}`,
        employees: employees,
        engineVersionId: selectedEngineVersionId ? Number(selectedEngineVersionId) : null,
        versionLabel:
          versionOptions.find((version) => String(version.id) === String(selectedEngineVersionId))
            ?.version_label || "",
      },
    });
  };

  const selectedMonthLabel = selectedMonth
    ? selectedMonth.replace("_", " ")
    : `${currentMonth} ${currentYear}`;

  if (loading) {
    return (
      <MainLayout>
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading payroll data...</p>
        </div>
      </MainLayout>
    );
  }

  const attendanceSummaryColumns = [
    {
      header: "Sr. No.",
      render: (_, index) => index + 1,
    },
    {
      header: "Emp. Code",
      key: "code",
    },
    {
      header: "Employee Name",
      key: "full_name",
    },
    {
      header: "Month",
      key: "month_name",
    },
    {
      header: "Year",
      key: "year",
    },
    {
      header: "Present",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (employee) => (
        <span className="badge badge-success">{employee.present}</span>
      ),
    },
    {
      header: "Half Day",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (employee) => (
        <span className="badge badge-info">{employee.half_day}</span>
      ),
    },
    {
      header: "Leave",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (employee) => (
        <span className="badge badge-warning">{employee.leave}</span>
      ),
    },
    {
      header: "Absent",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (employee) => (
        <span className="badge badge-danger">{employee.absent}</span>
      ),
    },
    {
      header: "Weekly Holidays",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (employee) => (
        <span className="badge badge-secondary">
          {employee.weekly_holidays}
        </span>
      ),
    },
  ];

  return (
    <MainLayout>
      <div className="generate-payroll-page">
        <div className="page-header-section">
          <div className="generate-payroll-heading">
            <h1>Generate Payroll</h1>
            <p className="subtitle">
              Reviewing attendance and payroll version for <strong>{selectedMonthLabel}</strong>
            </p>
          </div>

          <div className="generate-payroll-controls">
            <div className="header-actions">
              <div className="month-selector">
                <label>Select Month:</label>
                <select
                  className="form-control"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                >
                  <option value="">Select Month</option>

                  {financialMonths.map((item, index) => (
                    <option key={index} value={`${item.month}_${item.year}`}>
                      {item.month} {item.year}
                    </option>
                  ))}
                </select>
              </div>
              <div className="month-selector">
                <label>Payroll Version:</label>
                <select
                  className="form-control"
                  value={selectedEngineVersionId}
                  onChange={(e) => setSelectedEngineVersionId(e.target.value)}
                >
                  {versionOptions.map((version) => (
                    <option key={version.id} value={version.id}>
                      {version.version_label}
                      {version.is_latest ? " (Latest)" : ""}
                    </option>
                  ))}
                </select>
              </div>

              <div className="action-buttons">
                <button
                  type="button"
                  className="btn-process btn-process-payroll"
                  onClick={handleProcessPayroll}
                >
                  <i className="fas fa-calculator"></i>
                  <span>Process Payroll</span>
                </button>
              </div>
            </div>

            {existingRuns.length > 0 && (
              <p className="generate-payroll-runs-note">
                Existing runs for this month:{" "}
                {existingRuns
                  .map((run) => run.run_label)
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
          </div>
        </div>

        {/* Employee Attendance Table */}
        <div className="table-card">
          <div className="table-header">
            <h5>Employee Attendance Summary</h5>
            <p className="payroll-table-note">
              In case some of your employees don&apos;t show on this page,
              please make sure their salary structures have been created in{" "}
              <button
                type="button"
                className="payroll-table-note-link"
                onClick={() => navigate("/salary")}
              >
                Salary Structure
              </button>
              .
            </p>
            <p className="payroll-table-note">
              This summary is for <strong>{selectedMonthLabel}</strong> and includes attendance, leave, and holiday counts used before payroll calculation.
            </p>
          </div>
          <CommonTable
            columns={attendanceSummaryColumns}
            data={employees}
            tableClassName="payroll-table"
            wrapperClassName="table-responsive"
            noDataClassName="text-center"
            emptyMessage="No attendance records found for payroll generation"
            searchPlaceholder="Search by employee code, name, month..."
            getSearchText={(employee) =>
              [
                employee.code,
                employee.full_name,
                employee.month_name,
                employee.year,
              ]
                .filter(Boolean)
                .join(" ")
            }
          />
        </div>
      </div>
    </MainLayout>
  );
}

export default GeneratePayroll;
